import { AuthErrorCode } from '@kfolks/types'
import axios, { isAxiosError } from 'axios'

/**
 * Shared axios instance for all API requests.
 *
 * Credentials live in an HttpOnly `access_token` cookie set by the backend
 * `/auth/callback` endpoint. The browser attaches the cookie automatically
 * on every same-site or credentialed cross-site request, so the SPA never
 * reads or stores the JWT. `withCredentials: true` is what tells axios to
 * include cookies on cross-origin requests (without it, the browser strips
 * them for any request whose `Origin` differs from the page).
 */
export const apiClient = axios.create({
	baseURL: import.meta.env.VITE_API_URL || '/api',
	withCredentials: true,
	headers: {
		'Content-Type': 'application/json',
	},
})

// Response interceptor for terminal auth failures. The cookie is HttpOnly so
// there's nothing for JS to clear here — a 401 just means "your cookie is
// missing/expired, get a new one by re-logging." `AuthProvider` handles the
// `/auth/me` 401 path with a redirect to `/error/unauthorized`; this fallback
// catches stray API calls that aren't gated by `useAuth()`.
apiClient.interceptors.response.use(
	(response) => response,
	(error: unknown) => {
		if (isAxiosError(error)) {
			const status = error.response?.status
			const data = error.response?.data as { error?: { code?: string } } | undefined
			const errorCode = data?.error?.code

			if (errorCode === AuthErrorCode.ACCOUNT_INACTIVE) {
				window.location.href = '/error/inactive'
				return Promise.reject(error)
			}

			if (status === 401) {
				// `AuthProvider` owns the /auth/me 401 path (redirects to
				// /error/unauthorized). Skipping it here avoids a redirect loop
				// when the SPA mounts without a cookie (every reload of /login
				// re-mounts AuthProvider, which would fire /auth/me again).
				const url = error.config?.url ?? ''
				const pathname = window.location.pathname ?? ''
				const onAuthRoute = pathname === '/login' || pathname.startsWith('/error/')
				if (!url.includes('/auth/me') && !onAuthRoute) {
					window.location.href = '/login'
				}
			}
		}
		return Promise.reject(error)
	},
)
